import {Transfers, formatBank, formatBrokerage} from '@client/models/store/transfers';
import {
  WITHDRAWAL,
  DEPOSIT,
  LINKINGTYPE,
  TRANSFER_INITIATED_FROM_CODES,
  NEW_PLAID_ACCOUNT_ID,
  MICRO_DEPOSIT_PENDING_ACCOUNT_NAME,
  PLAID_ERROR_CODE,
} from '@client/util/constants/transfers';
import {Logger, Category} from '@simpleinvest/front-end-logger';
// eslint-disable-next-line import/no-cycle
import transferService from '@client/services/transfers';
// eslint-disable-next-line import/no-cycle
import investmentService from '@client/services/investment';
import {CUSTODIANS} from '@client/util/constants/custodian';
import {TRANSFER_DIRECTION, DATE_FORMAT_DB} from '@client/util/constants';
import {BankLinkRequestStatus, DelayedTransferRequestStatus} from '@simpleinvest/ms-custodian-sdk';
import TransferStatus from '@client/enums/transferStatus';
import {getDate, getTodaysTimestampInUTCMS} from '@client/util/dateHelper';
import {isTodayOrFuture} from '@client/util/dates';
import {toBackslashDate, toMoney, formatDateToDb} from '@client/util/format';
import {getMsApiGatewayHostname} from '@simpleinvest/ms-core-sdk';
import format from 'date-fns/format';
// eslint-disable-next-line import/no-cycle
import {plaidCreateOnExit} from '@client/store/modules/moveMoney/plaidCreateHelper';
import {displayErrorBanner} from '@client/events/notification-bus';

const logger = new Logger({
  category: Category.TransferMoney,
  fileName: 'transfers.js',
});

const filterAccounts = (allAccountsList, isLive) => {
  if (allAccountsList) {
    return allAccountsList.filter((account) => account.isLive === isLive);
  }

  return [];
};

export const getDepositDescription = (transfer) => ({
  depositDescription: `Deposit from <strong>${transfer.bankName || transfer.bankAccountType}  xxxxx${
    transfer.lastFourBankAccountNumber
  }</strong> into <strong>${transfer.name} ${
    transfer.accountNumber ? `xxxxx${transfer.accountNumber.slice(-4)}` : ''
  }</strong>.`,
});

export const getWithdrawalDescription = (transfer) => ({
  depositDescription: `Withdrawal from <strong>${transfer.name} ${
    transfer.accountNumber ? `xxxxx${transfer.accountNumber.slice(-4)}` : ''
  }</strong> into <strong>${transfer.bankName || transfer.bankAccountType}  xxxxx${
    transfer.lastFourBankAccountNumber
  }</strong>.`,
});

const verifiyDepositsHandleTimeout = async (payload) => {
  try {
    const result = await transferService.verifyDeposit(payload);

    if (!result || !('attemptId' in result) || !('isApproved' in result) || !('remainingAttempts' in result)) {
      throw new Error('invalid response from verify deposit call');
    }

    return result;
  } catch (error) {
    if (error.microserviceStatusCode === 504) {
      // bread crumbs
      logger.addBreadcrumb({
        callingFuncName: 'verifyDeposit',
        message: `timeout while checking account balance`,
      });
      // TBD post event on service bus
      // return isApproved - true - to caller
      const result = {
        attemptId: -1,
        isApproved: true,
        remainingAttempts: 2,
      };

      return result;
    }

    throw error;
  }
};

const stateObj = () => new Transfers();
const gettersObj = {
  previousYearTaxCutOffDate: (state) => {
    const cutOffDate = state.previousYearTaxDate || 0;

    return cutOffDate;
  },
  contributionYears: (state) => {
    const contributionYear = [{name: `Current Year`, value: `Current ${getDate().getFullYear()}`}];

    const now = getTodaysTimestampInUTCMS();
    const prevYearTaxCutoff = state.previousYearTaxDate;
    const onlyAllowCurrentYearContribution =
      state.iraConstraints?.contributionConstraints?.onlyAllowCurrentYearContribution;

    if (onlyAllowCurrentYearContribution === false && now < prevYearTaxCutoff) {
      contributionYear.push({name: `Previous Year`, value: `Previous ${getDate().getFullYear() - 1}`});
    }

    return contributionYear;
  },

  showFederalTax: (state) => {
    if (
      state.type === WITHDRAWAL &&
      state.isRetirementAccount &&
      state.iraConstraints.distributionConstraints &&
      state.distributionReason
    ) {
      const {validDistributionReasons} = state.iraConstraints.distributionConstraints;

      const distributionReason = validDistributionReasons.find((item) => item.reason === state.distributionReason);

      return distributionReason?.withholdingEnabled;
    }

    return false;
  },
  clientDirectedContributionTypes: (state) => state.clientContributionTypes,
  showStateTax: (state) => {
    if (
      state.type === WITHDRAWAL &&
      state.isRetirementAccount &&
      state.iraConstraints.distributionConstraints &&
      state.distributionReason
    ) {
      const {validDistributionReasons} = state.iraConstraints.distributionConstraints;

      const distributionReason = validDistributionReasons.find((item) => item.reason === state.distributionReason);

      return distributionReason?.stateWithholdingEnabled;
    }

    return false;
  },
  bankAccounts: (state) =>
    // eslint-disable-next-line unicorn/no-fn-reference-in-iterator
    state._accounts.bank.map((bank) => formatBank(bank)),
  showDistributionReason: (state, rootGetters) =>
    state.advisorInitiated && state.type === WITHDRAWAL && rootGetters.isIraAccount && state.distributionReason,
  isIraAccount: (state) => state.isRetirementAccount,
  isIraConstraintsSuccess: (state) => state.isIraConstraints,
  sourceAccounts: (state) =>
    // deposit: from bank to brokerage
    // withdrawal: from brokerage to bank
    // eslint-disable-next-line unicorn/no-fn-reference-in-iterator
    state.type === DEPOSIT
      ? state._accounts.bank.map((bank) => formatBank(bank))
      : state._accounts.brokerage.map((account) => formatBrokerage(account)),
  destinationAccounts: (state) =>
    // eslint-disable-next-line unicorn/no-fn-reference-in-iterator
    state.type === DEPOSIT
      ? state._accounts.brokerage.map((account) => formatBrokerage(account))
      : state._accounts.bank.map((bank) => formatBank(bank)),
  preTaxAccount: (state, getters, rootState) => {
    // eslint-disable-next-line unicorn/explicit-length-check
    if (!rootState.accountList.accountList || !rootState.accountList.accountList.length) {
      return [];
    }

    return rootState.accountList.accountList.filter((account) => account.account_type === 'Traditional IRA');
  },
  preTaxAccountStage: (state, getters, rootState) => {
    // eslint-disable-next-line unicorn/explicit-length-check
    if (!rootState.accountList.accountListStage || !rootState.accountList.accountListStage.length) {
      return [];
    }

    return rootState.accountList.accountListStage.filter((account) => account.account_type === 'Traditional IRA');
  },
  postTaxAccount: (state, getters, rootState) => {
    // eslint-disable-next-line unicorn/explicit-length-check
    if (!rootState.accountList.accountList || !rootState.accountList.accountList.length) {
      return [];
    }

    return rootState.accountList.accountList.filter((account) => account.account_type === 'Roth IRA');
  },
  postTaxAccountStage: (state, getters, rootState) => {
    // eslint-disable-next-line unicorn/explicit-length-check
    if (!rootState.accountList.accountListStage || !rootState.accountList.accountListStage.length) {
      return [];
    }

    return rootState.accountList.accountListStage.filter((account) => account.account_type === 'Roth IRA');
  },
  showPrevContributionYearHelpText(state, getters) {
    const now = getTodaysTimestampInUTCMS();

    const previousYearTaxCutOffDate = state.previousYearTaxDate;

    const moreThanOneYearAllowed = getters.contributionYears?.length > 1;

    return moreThanOneYearAllowed && now <= previousYearTaxCutOffDate;
  },
  prevContributionYearHelpText(state) {
    const {previousYearTaxDate} = state;

    const dt = new Date(previousYearTaxDate);
    const taxCutoffAsDateObj = new Date(dt.valueOf() + dt.getTimezoneOffset() * 60 * 1000);
    const dateCutoffString = format(taxCutoffAsDateObj, 'MMMM d');

    const helpText = `Contributions for the previous year can only be made on a one-time basis prior to the tax deadline of ${dateCutoffString}.`;

    const todayIsBeforeTaxCutoff = getDate() < previousYearTaxDate;
    const priorToTaxDeadlineText =
      'If you would like to make this type of contribution, please select a one-time transfer frequency.';

    return todayIsBeforeTaxCutoff ? `${helpText} ${priorToTaxDeadlineText}` : helpText;
  },
  plaidMetadata(state) {
    return state.plaidMetadata;
  },
  accessToken(state) {
    return state.accessToken;
  },
  transferFrequencyItems(state) {
    if (state.frequency) {
      return state.frequency.map((freq) => ({
        displayValue: freq.transferFrequency,
        code: freq.transferFrequencyCode,
      }));
    }

    return [];
  },

  showInsufficientFundsModal(state) {
    return state.showInsufficientFundsModal;
  },
  transferAttemptsRemaining(state) {
    return state.depositVerificationResponse.remainingAttempts;
  },
  hasSufficientFunds(state) {
    return state.depositVerificationResponse.isApproved;
  },
  isLinkPendingIdentity(state) {
    return state.linkNewBankAccountData?.identityApproved === false;
  },
  transferDetail(state) {
    return state.transferDetail;
  },
  plaidCompleteStatus(state) {
    return state.plaidCompleteStatus;
  },
  isRetirementAccount(state) {
    return state.isRetirementAccount;
  },
  advisorInitiated(state) {
    return state.advisorInitiated;
  },
  amount(state) {
    return state.amount;
  },
  from(state) {
    return state.from;
  },
  to(state) {
    return state.to;
  },
  transferFrequency(state) {
    return state.transferFrequency;
  },
  optOutFormChecked(state) {
    return state.optOutFormChecked;
  },
  date(state) {
    return state.date;
  },
  contributionYear(state) {
    return state.contributionYear;
  },
  fedTaxWithholdingValue(state) {
    return state.fedTaxWithholdingValue;
  },
  stateTaxWithholdingValue(state) {
    return state.stateTaxWithholdingValue;
  },
  contributionType(state) {
    return state.contributionType;
  },
  contributionTypeText(state) {
    return state.contributionTypeText;
  },
  destinationId(state) {
    return state.destinationId;
  },
  sourceId(state) {
    return state.sourceId;
  },
  microDeposit(state) {
    return state.microDeposit;
  },
  plaidData(state) {
    return state.plaidData;
  },
  linkNewBankAccountData(state) {
    return state.linkNewBankAccountData;
  },
  linkedBankCustodianAccounts(state) {
    return state.linkedBankCustodianAccounts;
  },
  distributionReasonText(state) {
    return state.distributionReasonText;
  },
  selectedBank(state, getters) {
    const bankAccountId = state.type === WITHDRAWAL ? state.destinationId : state.sourceId;
    const accounts = state.type === WITHDRAWAL ? getters.destinationAccounts : getters.sourceAccounts;

    return accounts.find((account) => account.id === bankAccountId)?.formattedName || 'bank';
  },
  selectedInvestmentAccount(state, getters) {
    const brokerageId = state.type === WITHDRAWAL ? state.sourceId : state.destinationId;
    const accounts = state.type === WITHDRAWAL ? getters.sourceAccounts : getters.destinationAccounts;

    return accounts.find((account) => account.id === brokerageId)?.formattedName || 'brokerage';
  },
  type(state) {
    return state.type;
  },
  depositAttemptIds(state) {
    return state.depositAttemptIds;
  },
  bankAccountId(state) {
    return state.bankAccountId;
  },
  failedToFetchTransfer(state) {
    return state.failedToFetchTransfer;
  },
  requestedTransfers(state) {
    return state.requestedTransfers;
  },
  findRequestedTransferByAccountId(state, getters) {
    return (accountId) => getters.requestedTransfers.find((item) => Number(item.accountId) === Number(accountId));
  },
  getRequestedTransferAlertLinkText(state, getters) {
    return (accountId) => {
      const requestedTransfer = getters.findRequestedTransferByAccountId(accountId);

      // eslint-disable-next-line no-undefined
      if (requestedTransfer !== undefined) {
        const {transferDirection, bankAccountId} = requestedTransfer || {};

        if (!bankAccountId) {
          return 'Pending Bank Link';
        }

        const transferDirectionText = transferDirection
          ? TRANSFER_DIRECTION[transferDirection.transferDirectionId]
          : 'Transfer';

        return `${transferDirectionText} Requested`;
      }

      return null;
    };
  },
  requestedBankLinks(state) {
    return state.requestedBankLinks;
  },
  findRequestedBankLinkByAccountId(state, getters) {
    return (accountId) => getters.requestedBankLinks.find((item) => Number(item.accountId) === Number(accountId));
  },
  summaryTransfer(state) {
    return state.summaryTransfer;
  },
  delayedTransfers(state) {
    return state.delayedTransfers;
  },
  delayedTransfersByTransferId: (state, getters) => (transferId) =>
    getters.delayedTransfers.filter(
      (delayed) => delayed.transferId === transferId && delayed.status === DelayedTransferRequestStatus.Pending
    ),

  // eslint-disable-next-line max-lines-per-function
  scheduledApexTransactionDetails(state, getters, rootState, rootGetters) {
    const {summaryTransfer, delayedTransfersByTransferId} = getters;
    const {'bankAccount/pendingActions': pendingActions, 'bankAccount/pendingReason': getPendingReason} = rootGetters;

    const filtered =
      // eslint-disable-next-line unicorn/no-array-reduce
      summaryTransfer?.reduce((transfers, detail) => {
        const isApexWithData =
          detail.transferStatusCode === TransferStatus.liquidation
            ? detail.nextTransferDate
            : detail.bankAccountId && detail.nextTransferDate;

        if (!isApexWithData) {
          return transfers;
        }

        const delayedInstances = delayedTransfersByTransferId(detail.id).length;
        const futureOrDelayed = isTodayOrFuture(detail.nextTransferDate) || delayedInstances;

        if (!futureOrDelayed) {
          return transfers;
        }

        const nextTransferDate = toBackslashDate(detail.nextTransferDate);
        const {microDepositPending, identityPending} = pendingActions(detail.bankAccountId);
        const pendingReason = getPendingReason(detail.bankAccountId);

        const transfer = {
          id: detail.id,
          contributionYear: detail.contributionYear,
          accountId: detail.accountId,
          account: detail.name,
          accountNumber: detail.accountNumber,
          bankName: detail.bankName,
          bankAccountType: detail.bankAccountType,
          lastFourBankAccountNumber: detail.lastFourBankAccountNumber,
          amount: toMoney(detail.transferAmount),
          transferAmount: detail.transferAmount,
          frequency: detail.transferFrequency,
          date: nextTransferDate,
          direction: detail.transferDirection.transferDirectionId,
          identityPending,
          microDepositPending,
          pendingReason,
          delayedInstances,
          status: detail.transactionStatus,
          fedTax: detail.fedTaxWithholdingValue,
          stateTax: detail.stateTaxWithholdingValue,
          transferStatusCode: detail.transferStatusCode,
          transferMode: detail.transferMode,
          stateTaxWithholdingValueType: detail.stateTaxWithholdingValueType,
        };

        // eslint-disable-next-line unicorn/prefer-spread
        return transfers.concat(transfer);
      }, []) || [];

    return filtered;
  },

  previousYearContributionAllowed(state) {
    // eslint-disable-next-line no-unsafe-optional-chaining
    const {onlyAllowCurrentYearContribution} = state.iraConstraints?.contributionConstraints;

    return !onlyAllowCurrentYearContribution;
  },
  brokerageAccountIds(state) {
    return state._accounts?.brokerage?.map((account) => account.accountId);
  },
  isExemptedValue(state) {
    return state.isExempted;
  },
  stateTaxWithholdingData(state) {
    return state.stateTaxWithholdingData;
  },
};

const actions = {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  checkAccountInLiquidation: ({dispatch}, payload) =>
    payload.accounts
      .filter((account) => account.id === payload.value)
      .filter((account) => account.isAccountInLiquidationProcess)
      .map(() => {
        displayErrorBanner(
          'Sorry, you will not be able to set up a transfer for an account for which liquidation is in process.'
        );

        return true;
      }),
  setTransfer({commit}, payload) {
    commit('SET_TRANSFER', payload);
  },
  setStateWithholdingData({commit}, payload) {
    commit('SET_STATE_WITHHOLDING', payload);
  },
  setPlaidComplete({commit}, payload) {
    commit('SET_PLAID_COMPLETE', payload);
  },
  setSourceId({commit}, payload) {
    commit('SET_SOURCE_ID', payload);
  },
  setDestinationId({commit}, payload) {
    commit('SET_DESTINATION_ID', payload);
  },
  setDate({commit}, payload) {
    commit('SET_DATE', payload);
  },
  setFirstTimeLinkBank({commit}, payload) {
    commit('SET_FIRST_TIME_LINK_BANK', payload);
  },
  async getRequestedTransfers({commit, rootGetters}) {
    const result = await transferService.fetchAllByUserAndStatus({
      clUserId: rootGetters['cognitoUser/userId'],
      transferStatus: [TransferStatus.requested, TransferStatus.clientComplete],
    });
    const {data} = result;

    commit('SET_REQUESTED_TRANSFERS', data);

    return data;
  },
  async getRequestedBankLinks({commit, rootGetters}, status = BankLinkRequestStatus.Pending) {
    const data = await transferService.fetchBankLinkRequestsByStatus({
      clUserId: rootGetters['cognitoUser/userId'],
      status,
    });

    commit('SET_REQUESTED_BANK_LINKS', data);

    return data;
  },
  setContributionType({commit}, payload) {
    commit('SET_CONTRIBUTION_TYPE', payload);
  },
  setOptOutFormChecked({commit}, payload) {
    commit('SET_OPT_OUT_FORM_CHECKED', payload);
  },
  async getTransferRules({commit, dispatch, rootGetters}) {
    const transferStatus = [TransferStatus.complete, TransferStatus.liquidation];
    const result = await transferService.fetchAllByUserAndStatus({
      clUserId: rootGetters['user/clUserId'],
      transferStatus,
    });

    if (!result.data) {
      throw new Error('Failed to get transfer rules');
    }

    try {
      await dispatch('getDelayedTransfers');
    } catch (error) {
      logger.addBreadcrumb({
        callingFuncName: 'getTransferRules',
        message: `error getting delayed transfers`,
      });

      // If we can't fetch the delayed transfers, it's not the end of the world, but we want to know about it.
      logger.logException({error});
    }

    commit('SET_TRANSFER_RULES', result.data);

    return result.data;
  },
  async getTransferFrequencies({commit}) {
    const result = await transferService.getTransferFrequencies({custodian: 'APEX'});

    if (!result) {
      throw new Error('Failed to get transfer frequencies');
    }

    commit('SET_FREQUENCY', result);

    return result;
  },
  // getWithdrawalAmtTypes({commit}) {
  //   const result = [];

  //   result[0] = {withdrawalAmtCode: '%', withdrawalAmtDesc: '%'};
  //   result[1] = {withdrawalAmtCode: '$', withdrawalAmtDesc: '$'};

  //   commit('SET_WITHDRAWAL_AMOUNT_TYPES', result);

  //   return result;
  // },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async deleteTransfer({dispatch}, transferId) {
    await transferService.delete({transferId});
  },
  async getTransferRequestList({dispatch}) {
    await dispatch('getTransferRules');
    await dispatch('getRequestedTransfers');
  },
  async updateBankLinkRequest({dispatch, rootGetters}, {requestId, ...payload}) {
    await transferService.updateUserBankLinkRequestById({
      clUserId: rootGetters['cognitoUser/userId'],
      bankAccountLinkRequestId: requestId,
      status: payload.status,
    });

    await dispatch('getRequestedBankLinks');
  },
  async saveTransferData({dispatch, getters, rootGetters}, payload) {
    const result = await transferService.save(payload.data);

    if (!result) {
      throw new Error('Failed to save transfer data');
    }

    if (getters.type === DEPOSIT && rootGetters['settings/requiresBalanceCheck']) {
      // not waiting for this to resolve
      dispatch('linkDepositToBalanceCheck', result);
    }

    return result;
  },
  async linkDepositToBalanceCheck({commit, getters, rootGetters}, payload) {
    const bankAccountId = getters.microDeposit ? 0 : getters.bankAccountId;
    const accountId = getters.to.id;
    const depositAttempts = getters.depositAttemptIds;
    const attemptIds = depositAttempts.get(`${accountId}_${bankAccountId}`);

    if (attemptIds?.length > 0) {
      const requestBody = {
        transferId: payload.clTransferRequest.id,
        clUserId: rootGetters['user/clUserId'],
        attemptIds,
      };

      await transferService.associateTransferIdWithBalanceCheckAttempts(requestBody);
    }

    const attemptIdIdentifier = {
      bankAccountId,
      accountId,
    };

    commit('CLEAR_DEPOSIT_ATTEMPT_ID', attemptIdIdentifier);
  },
  async setTransferData({dispatch, getters, rootGetters}, payload) {
    const result = await transferService.setTransferData(payload);

    if (!result) {
      throw new Error('Failed to set transfer data');
    }

    if (getters.type === DEPOSIT && rootGetters['settings/requiresBalanceCheck']) {
      // not waiting for this to resolve
      dispatch('linkDepositToBalanceCheck', result);
    }

    return result;
  },
  async getBankData({commit}, {publicToken, accountId, institution}) {
    const result = await transferService.getBankData({publicToken, accountId});

    commit('SET_BANK_DATA', result);
    commit('SET_BANK_NAME', institution);
  },
  async getAccessToken({commit}, {publicToken}) {
    const result = await transferService.getAccessToken({publicToken});

    commit('SET_ACCESS_TOKEN', {accessToken: result});
  },
  clearAccessToken({commit}) {
    commit('SET_ACCESS_TOKEN', {accessToken: null});
  },
  // eslint-disable-next-line complexity, max-statements, max-lines-per-function
  async getIraConstraints({commit, dispatch}, {accNum, direction, taxId}) {
    let result = null;

    if (direction === DEPOSIT) {
      result = await dispatch('getDepositIraConstraints', {commit}, {accNum, taxId});

      return result;
    }

    result = await dispatch('getWithdrawalIraConstraints', {commit}, {accNum, taxId});

    return result;
  },
  // eslint-disable-next-line max-statements
  async getWithdrawalIraConstraints({commit}, {accNum, taxId}) {
    let result = null;
    const returnedVal = {callWasSuccessful: true, errorCode: null};

    logger.addBreadcrumb({
      callingFuncName: 'getWithdrawalIraConstraints',
      message: `Transfer type: Withdrawal`,
    });

    try {
      result = await investmentService.getWithdrawalIraConstraints(accNum);
      commit('SET_NEW_IRA_CONSTRAINTS', result);
    } catch (error) {
      logger.addBreadcrumb({
        callingFuncName: 'getWithdrawalIraConstraints',
        message: `getIraConstraintsOutgoing error caught ${JSON.stringify(error)}`,
      });
      logger.logException({error});

      returnedVal.callWasSuccessful = false;
      returnedVal.errorCode = error?.name;

      return returnedVal;
    }
    if (taxId === 3 && (!result.retirementAccount || !result.distributionConstraints)) {
      returnedVal.callWasSuccessful = false;

      return returnedVal;
    }

    let distributionReason = null;

    // new distributionReasons's schema is Array
    if (result.distributionConstraints) {
      const {validDistributionReasons} = result.distributionConstraints;
      const reasons = validDistributionReasons.map((item) => item.reason);

      distributionReason = reasons.includes('NORMAL') ? 'NORMAL' : 'PREMATURE';
    }

    commit('SET_DISTRIBUTION_REASON', {distributionReason});

    return returnedVal;
  },

  async getBankAccountPlaidLinkStatus({rootState}, {accountId}) {
    let result = null;
    const returnedVal = {callWasSuccessful: true, errorCode: null};

    logger.addBreadcrumb({
      callingFuncName: 'getBankAccountPlaidLinkStatus',
      message: `Transfer type: Deposit`,
    });

    try {
      result = await transferService.fetchBankAccountPlaidLinkStatus(rootState.user.id, accountId);

      if (!result.active) {
        returnedVal.callWasSuccessful = false;
        if (result.status === PLAID_ERROR_CODE.ITEM_LOGIN_REQUIRED) {
          returnedVal.errorCode = PLAID_ERROR_CODE.ITEM_LOGIN_REQUIRED;
        }

        if (result.status === PLAID_ERROR_CODE.INVALID_ACCOUNT_ID) {
          returnedVal.errorCode = PLAID_ERROR_CODE.INVALID_ACCOUNT_ID;
        }
      }
    } catch (error) {
      logger.addBreadcrumb({
        callingFuncName: 'getBankAccountPlaidLinkStatus',
        message: `getBankAccountPlaidLinkStatus error caught ${JSON.stringify(error)}`,
      });
      logger.logException({error});

      returnedVal.callWasSuccessful = false;
      returnedVal.errorCode = error?.microserviceStatusCode;
    }

    return returnedVal;
  },

  async getDepositIraConstraints({commit}, {accNum, taxId}) {
    let result = null;
    const returnedVal = {callWasSuccessful: true, errorCode: null};

    logger.addBreadcrumb({
      callingFuncName: 'getDepositIraConstraints',
      message: `Transfer type: Deposit`,
    });

    try {
      result = await investmentService.getDepositIraConstraints(accNum);

      commit('SET_NEW_IRA_CONSTRAINTS', result);
      if (taxId === 3 && (!result.retirementAccount || !result.contributionConstraints)) {
        returnedVal.callWasSuccessful = false;

        return returnedVal;
      }
    } catch (error) {
      logger.addBreadcrumb({
        callingFuncName: 'getDepositIraConstraints',
        message: `getIraConstraintsIncoming error caught ${JSON.stringify(error)}`,
      });
      logger.logException({error});

      returnedVal.callWasSuccessful = false;
      returnedVal.errorCode = error?.microserviceStatusCode;

      return returnedVal;
    }

    return returnedVal;
  },

  // eslint-disable-next-line max-lines-per-function, require-await
  async triggerPlaid({rootState, rootGetters, dispatch, commit}) {
    const {link_token: token} = await transferService.createLinkToken({
      clUserId: Number(rootGetters['cognitoUser/userId']),
      clientName: rootState.riaName,
      webhookUrl: `${getMsApiGatewayHostname()}/plaid/webhook`,
    });

    // eslint-disable-next-line max-lines-per-function
    return new Promise((resolve, reject) => {
      const handler = Plaid.create({
        token,
        selectAccount: true,
        // TODO: move onEvent to plaidCreateHelper.ts
        onEvent: (eventName, metadata) => {
          commit('SET_PLAID_METADATA', metadata);
          if (metadata.request_id) {
            commit('SET_PLAID_REQUEST_ID', {
              plaidRequestId: metadata.request_id,
            });
          }
        },
        // TODO: move onSuccess to plaidCreateHelper.ts
        onSuccess: async (publicToken, metadata) => {
          commit('SET_PLAID_METADATA', metadata);

          await dispatch('getAccessToken', {publicToken});

          // eslint-disable-next-line no-negated-condition, unicorn/no-negated-condition
          if (metadata.accounts[0].verification_status !== null) {
            commit('SET_BANK_ACCOUNTS', {
              payload: [],
              type: 'Microdeposit',
            });
            commit('SET_MICRODEPOSIT', true);
          } else {
            await dispatch('getBankData', {
              publicToken,
              accountId: metadata.account_id,
              institution: metadata.institution.name,
            });
            commit('SET_BANK_ACCOUNTS', {
              payload: [],
              type: 'Plaid',
            });
            // trigger create new bank link
            commit('SET_MICRODEPOSIT', false);
          }

          resolve();
        },
        onExit: (error, metadata) => {
          // TODO: Do we need to handle INVALID_LINK_TOKEN errors since we're creating a new instance of Link each time?
          plaidCreateOnExit({functionName: 'triggerPlaid', commit, error, metadata});
          if (error) {
            const throwable =
              error instanceof Error ? error : new Error(String(error.error_message || error.message || error));

            reject(throwable);
          } else {
            resolve();
          }
        },
      });

      handler.open();
    });
  },
  async getBankAccounts({commit, rootState}, payload) {
    const result = await transferService.fetchUserBankAccounts(rootState.user.id, payload);

    if (!result) {
      throw new Error('Failed to get bank accounts');
    }

    // eslint-disable-next-line no-param-reassign
    result.forEach((bankAccount) => delete bankAccount.bankAccountNumber);

    commit('SET_BANK_ACCOUNTS', {payload: [...result]});
    commit(`SET_${rootState.transfers.type === WITHDRAWAL ? 'DESTINATION' : 'SOURCE'}_BANK`, result);

    return result;
  },
  async getCustodianAccounts({commit, rootGetters, getters}) {
    const result = await transferService.getCustodianAccounts({clUserId: rootGetters['cognitoUser/userId']});

    if (!result) {
      throw new Error('Failed to get custodian accounts');
    }

    commit('SET_BROKERAGE_ACCOUNTS', result);
    if (getters.type !== '401k') {
      commit(`SET_${getters.type === WITHDRAWAL ? 'SOURCE' : 'DESTINATION'}_BANK`, result);
    }
  },
  // eslint-disable-next-line require-await
  async hasCustodianAccounts({commit, rootState}) {
    let disableMoveMoney = false;
    let message = '';
    const {accountList} = rootState.accountList;
    const accountListLive = filterAccounts(accountList, true);
    const accountListStage = filterAccounts(accountList, false);

    // eslint-disable-next-line unicorn/explicit-length-check
    if (!(accountListLive.length || accountListStage.length)) {
      disableMoveMoney = true;
      message = 'NewOpen';
    }

    // eslint-disable-next-line unicorn/explicit-length-check
    if (!accountListLive.length && accountListStage.length) {
      disableMoveMoney = true;
      message = 'Pending';
      if (accountListStage.length === 1) {
        const iskyc = accountListStage.find((account) => account.custodianId === 4 || account.custodianId === 5);

        if (iskyc) {
          message = 'KYC/Sketch';
        }
      }
    }

    commit('SET_DISABLE_MOVEMONEY', {disableMoveMoney, message});
  },
  async linkBankAccount({commit}, {accountId, payload}) {
    try {
      const result = await transferService.linkBankAccount({
        accountId,
        bankLinkRequest: payload,
      });

      commit('SET_LINK_BANK_ACCOUNT', result);

      return result;
    } catch (error) {
      throw new Error(error);
    }
  },
  existingBankLink({rootGetters}, {accountId}) {
    const clUserId = rootGetters['cognitoUser/userId'];

    return transferService.fetchLinkedBankAccounts({clUserId, accountId});
  },
  async fetchLinkedBankCustodianAccounts({commit, rootGetters}) {
    const {'cognitoUser/userId': clUserId} = rootGetters;
    const response = await transferService.getLinkedBankAccounts(clUserId);

    if (!response.data) {
      logger.addBreadcrumb({
        callingFuncName: 'fetchLinkedBankCustodianAccounts',
        message: 'fetchLinkedBankCustodianAccounts error caught',
      });
      throw new Error('Failed to get linked banks and custodian accounts');
    }

    const {data} = response.data;

    data.bankAccounts.forEach((bankAccount) => {
      // eslint-disable-next-line no-param-reassign
      delete bankAccount.bankAccountNumber;
    });
    commit('SET_LINKED_BANK_CUSTODIAN_ACCOUNTS', data);
  },

  async getTransferDetail({commit, dispatch, rootState}, {transferId}) {
    try {
      const result = await transferService.fetchByUserAndId({clUserId: rootState.user.id, transferId});

      const transfer = result.data[0];
      const today = format(new Date(), DATE_FORMAT_DB);
      const transferDate = formatDateToDb(transfer.nextTransferDate);

      if (transferDate < today) {
        transfer.nextTransferDate = today;
      }

      commit('SET_TRANSFER_DETAIL', transfer);
      // Do not set the advisor initiated flag (not even to false) if transferInitiatedFromCode is null
      // This is because we will fall back to the query param value (initiator) for old transfers where
      // transferInitiatedFromCode is not set
      if (transfer?.transferInitiatedFromCode) {
        commit('SET_ADVISOR_INITIATED', transfer.transferInitiatedFromCode === TRANSFER_INITIATED_FROM_CODES.advisor);
      }

      dispatch('setTransferCustodian', transfer.custodianName);
    } catch {
      commit('SET_FAILED_TO_FETCH_TRANSFER', true);
    }
  },
  async getClientDirectedContributionType({commit}, {accountTypeId}) {
    try {
      const contributionTypes = await transferService.getClientDirectedContributionTypes(accountTypeId);

      commit('SET_CLIENT_CONTRIBUTION_TYPE', contributionTypes);
    } catch (error) {
      logger.addBreadcrumb({
        callingFuncName: 'getClientDirectedContributionType',
        message: 'getClientDirectedContributionType error caught',
      });
      logger.logException({error});
    }
  },
  async getDistributionReasonText({commit, rootState}) {
    try {
      const apexLabels = await transferService.getApexLabels();

      const reasonText = apexLabels.find(
        (label) => label.apexLabel === rootState.transfers.transferDetail.distributionReason
      );

      commit('SET_DISTRIBUTION_TEXT', reasonText.uiLabel);
    } catch (error) {
      throw new Error(error);
    }
  },
  async getContributionTypeText({commit, rootState}) {
    try {
      const apexLabels = await transferService.getApexLabels();

      const typeText = apexLabels.find((label) => label.apexLabel === rootState.transfers.transferDetail.contributionType);

      commit('SET_CONTRIBUTION_TEXT', typeText.uiLabel);
    } catch (error) {
      throw new Error(error);
    }
  },
  setTransferCustodian({commit}, payload) {
    let custodianCode;

    switch (payload) {
      case CUSTODIANS.FIDELITY.name: {
        custodianCode = CUSTODIANS.FIDELITY.code;
        break;
      }
      case CUSTODIANS.USBANK.name: {
        custodianCode = CUSTODIANS.USBANK.code;
        break;
      }
      case CUSTODIANS.BNYMELLON.name: {
        custodianCode = CUSTODIANS.BNYMELLON.code;
        break;
      }
      case CUSTODIANS.STATESTREET.name: {
        custodianCode = CUSTODIANS.STATESTREET.code;
        break;
      }
      case CUSTODIANS.ROBUSTWEALTH.name: {
        custodianCode = CUSTODIANS.ROBUSTWEALTH.code;
        break;
      }
      case CUSTODIANS.APEX.name: {
        custodianCode = CUSTODIANS.APEX.code;
        break;
      }
      default: {
        throw new Error('Invalid custodian name.');
      }
    }

    const custodianInfo = {
      custodianName: payload,
      custodianCode,
    };

    commit('SET_TRANSFER_CUSTODIAN_INFO', custodianInfo);
  },
  clearTransferInformation(context) {
    context.commit('SET_CLEAR');
  },
  setIsIraAccount({commit}, payload) {
    commit('SET_IS_IRA_ACCOUNT', payload);
  },
  async verifyDeposit({commit}, payload) {
    const result = await verifiyDepositsHandleTimeout(payload);

    const attemptLog = {
      attemptId: result.attemptId,
      bankAccountId: payload.bankAccountId,
      accountId: payload.accountId,
    };

    commit('SET_DEPOSIT_VERIFICATION_RESPONSE', result);
    commit('SET_SHOW_INSUFFICIENT_FUNDS_MODAL', !result.isApproved);
    commit('SAVE_DEPOSIT_ATTEMPT_ID', attemptLog);

    return result;
  },
  async getAccountLimitsMS({commit}, {accountType, year}) {
    try {
      const accountLimits = await transferService.getAccountLimits(accountType, year);

      commit('PREVIOUS_YEAR_CUTOFF_TAX_DATE', accountLimits);
    } catch (error) {
      logger.logException({error});
    }
  },
  setShowInsufficientFundsModal({commit}, payload) {
    commit('SET_SHOW_INSUFFICIENT_FUNDS_MODAL', payload);
  },
  setType({commit}, payload) {
    commit('SET_TRANSFER_TYPE', payload);
  },
  setFailedToFetchTransfer({commit}, payload) {
    commit('SET_FAILED_TO_FETCH_TRANSFER', payload);
  },
  async getDelayedTransfers({commit, rootGetters}) {
    const clUserId = rootGetters['user/clUserId'];
    const data = await transferService.getDelayedTransfers({clUserId});

    commit('SET_DELAYED_TRANSFERS', data);
  },

  async getTransactionValidationFlag(_, {transactionDate, transactionDirection}) {
    const {data: resp} = await transferService.getTransactionValidationFlag(transactionDate, transactionDirection);

    return resp.data?.isTransactionEditable;
  },
};

const mutations = {
  SET_FAILED_TO_FETCH_TRANSFER(state, payload) {
    state.failedToFetchTransfer = payload;
  },
  SET_SOURCE_ID(state, payload) {
    state.sourceId = payload;
  },
  SET_DESTINATION_ID(state, payload) {
    state.destinationId = payload;
  },
  SET_DATE(state, payload) {
    state.date = payload;
  },
  SET_TRANSFER(state, transfer) {
    Object.assign(state, transfer);
  },
  SET_STATE_WITHHOLDING(state, payload) {
    state.stateTaxWithholdingData = payload;
  },
  SET_REQUESTED_TRANSFERS(state, transfers) {
    state.requestedTransfers = transfers;
  },
  SET_REQUESTED_BANK_LINKS(state, links) {
    state.requestedBankLinks = links;
  },
  SET_TRANSFER_RULES(state, data) {
    state.summaryTransfer = data;
  },
  SET_FREQUENCY(state, data) {
    state.frequency = data;
  },
  SET_CURRENT_DATA(state, payload) {
    state.current = payload;
  },
  SET_TRANSFER_TYPE(state, payload) {
    state.type = payload;
  },
  SET_BANK_NAME(state, data) {
    state.plaidData.institution = data;
  },
  SET_BANK_DATA(state, data) {
    state.plaidData = data;
  },
  SET_ACCESS_TOKEN(state, data) {
    state.accessToken = data.accessToken;
  },
  SET_IRA_CONSTRAINTS(state, data) {
    state.iraConstraints = data;
    state.isRetirementAccount = data.isRetirementAccount;
    state.isIraConstraints = true;
  },
  SET_NEW_IRA_CONSTRAINTS(state, data) {
    state.iraConstraints = data;
    state.isRetirementAccount = data.retirementAccount;
    state.isIraConstraints = true;
  },
  SET_CONTRIBUTION_TYPE(state, data) {
    state.transferDetail.contributionType = data.contributionType;
    state.contributionTypeText = data.contributionTypeText;
  },
  SET_DISTRIBUTION_REASON(state, data) {
    state.distributionReason = data.distributionReason;
  },
  SET_PLAID_REQUEST_ID(state, data) {
    state.plaidRequestId = data.plaidRequestId;
  },
  SET_TRANSFER_FREQUENCY(state, payload) {
    state.transferFrequency = payload;
  },
  SET_OPT_OUT_FORM_CHECKED(state, data) {
    state.optOutFormChecked = data;
  },
  SET_TRANSFER_CONTRIBUTION_YEAR(state, payload) {
    state.contributionYear = payload;
  },
  SET_SOURCE_BANK(state, payload) {
    state.sourceBankAccounts = payload;
  },
  SET_DESTINATION_BANK(state, payload) {
    state.destinationBankAccounts = payload;
  },
  SET_BANK_ACCOUNTS(state, {payload, type}) {
    if (type !== LINKINGTYPE.MICRODEPOSIT && type !== LINKINGTYPE.PLAID) {
      state._accounts.bank = payload;
    } else {
      const data = state._accounts.bank;

      data.push({
        id: NEW_PLAID_ACCOUNT_ID,
        bankName: type === LINKINGTYPE.MICRODEPOSIT ? MICRO_DEPOSIT_PENDING_ACCOUNT_NAME : state.plaidData.institution,
        bankAccountType: '',
        bankAccountNumber: type === LINKINGTYPE.MICRODEPOSIT ? '' : state.plaidData.account,
      });
      state._accounts.bank = data;
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      state.type === 'Withdrawal' ? (state.destinationId = NEW_PLAID_ACCOUNT_ID) : (state.sourceId = NEW_PLAID_ACCOUNT_ID);
    }
  },
  SET_BROKERAGE_ACCOUNTS(state, payload) {
    state._accounts.brokerage = payload;
  },
  SET_DISABLE_MOVEMONEY(state, payload) {
    state.disableMoveMoney = payload;
  },
  SET_LINK_BANK_ACCOUNT(state, payload) {
    state.linkNewBankAccountData = payload;
  },
  SET_PLAID_METADATA(state, payload) {
    state.plaidMetadata = payload;
  },
  SET_MICRODEPOSIT(state, payload) {
    state.microDeposit = payload;
  },
  SET_PLAID_COMPLETE(state, status) {
    state.plaidCompleteStatus = status;
  },
  SET_LINKED_BANK_CUSTODIAN_ACCOUNTS(state, payload) {
    state.linkedBankCustodianAccounts = payload;
  },
  SET_TRANSFER_DETAIL(state, payload) {
    state.transferDetail = payload;
    const directionId = payload.transferDirection.transferDirectionId;

    if (directionId === 1) {
      state.sourceId = payload.bankAccountId;
      state.destinationId = payload.accountId;
    } else {
      state.sourceId = payload.accountId;
      state.destinationId = payload.bankAccountId;
    }

    const selectedBrokerageAccount = state._accounts.brokerage.find((i) => i.accountId === payload.accountId);

    state.isIra = selectedBrokerageAccount.isIra;
  },
  SET_CLEAR(state) {
    state.sourceId = '';
    state.destinationId = '';
    state.from = [];
    state.to = [];
    state._accounts.bank = [];
    state.iraConstraints = [];
    state.transferDetail = {
      custodianName: null,
      custodianCode: null,
      contrubtionType: null,
    };
    state.contritutionTypeTest = null;
    state.isRetirementAccount = false;
    state.type = '';
    state.microDeposit = false;
    state.advisorInitiated = false;
    state.isIra = false;
    state.isEdit = false;
    state.isIraConstraints = false;
  },
  SET_FUNDING_TYPE(state, fundingType) {
    state.rollover = {fundingType};
  },
  SET_FIRST_TIME_LINK_BANK(state, needbanklink) {
    state.firstTimeLinkBank = needbanklink;
  },
  SET_ADVISOR_INITIATED(state, isAdvisorInitiated) {
    state.advisorInitiated = isAdvisorInitiated;
  },
  SET_ADVISOR_INITIATED_ON_EDIT(state, payload) {
    state.advisorInitiated = payload.initiator === 'advisor';
    state.isEdit = true;
  },
  SET_DISTRIBUTION_TEXT(state, payload) {
    state.distributionReasonText = payload;
  },
  SET_CONTRIBUTION_TEXT(state, payload) {
    state.contributionTypeText = payload;
  },
  SET_TRANSFER_CUSTODIAN_INFO(state, payload) {
    state.transferDetail.custodianName = payload.custodianName;
    state.transferDetail.custodianCode = payload.custodianCode;
  },
  SET_CLIENT_CONTRIBUTION_TYPE(state, payload) {
    state.clientContributionTypes = payload;
  },
  SET_IS_IRA_ACCOUNT(state, payload) {
    state.isIra = payload;
  },
  SET_DEPOSIT_VERIFICATION_RESPONSE(state, payload) {
    state.depositVerificationResponse = payload;
  },
  SET_SHOW_INSUFFICIENT_FUNDS_MODAL(state, payload) {
    state.showInsufficientFundsModal = payload;
  },
  SAVE_DEPOSIT_ATTEMPT_ID(state, {attemptId, bankAccountId, accountId}) {
    const {depositAttemptIds} = state;

    if (depositAttemptIds.has(`${accountId}_${bankAccountId}`)) {
      const existingAttempts = depositAttemptIds.get(`${accountId}_${bankAccountId}`);

      existingAttempts.push(attemptId);
      depositAttemptIds.set(`${accountId}_${bankAccountId}`, existingAttempts);
    } else {
      depositAttemptIds.set(`${accountId}_${bankAccountId}`, [attemptId]);
    }

    state.depositAttemptIds = depositAttemptIds;
  },
  CLEAR_DEPOSIT_ATTEMPT_ID(state, {bankAccountId, accountId}) {
    const {depositAttemptIds} = state;

    depositAttemptIds.delete(`${accountId}_${bankAccountId}`);
    state.depositAttemptIds = depositAttemptIds;
  },

  SET_DELAYED_TRANSFERS(state, payload) {
    state.delayedTransfers = payload;
  },
  PREVIOUS_YEAR_CUTOFF_TAX_DATE(state, accounts) {
    const previousYearTaxDate = accounts[0].previousYearContributionDeadline;

    state.previousYearTaxDate = new Date(previousYearTaxDate).getTime();
  },
};

export default {
  namespaced: true,
  state: stateObj,
  getters: gettersObj,
  actions,
  mutations,
};
